import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Shield, CheckCircle, Loader2, ArrowLeft, AlertTriangle, LogIn } from "lucide-react";
import { useAccountUpgrade } from "@/hooks/use-account-upgrade";

interface AuthUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  onUpgradeComplete?: () => void;
  /**
   * When true, the modal is operated as "sign in with existing account".
   * If the submitted email is NOT registered (server returns mode=upgrade),
   * show a "no account found" state instead of the normal success screen.
   * Used from the welcome flow where there is no anonymous user to link.
   */
  loginOnly?: boolean;
}

export function AuthUpgradeModal({
  isOpen,
  onClose,
  currentUser,
  onUpgradeComplete,
  loginOnly = false,
}: AuthUpgradeModalProps) {
  const { step, email, mode, error, setEmail, sendLink, reset, isPending } = useAccountUpgrade();

  // If user is already upgraded, show a different message
  const alreadyUpgraded = currentUser && !currentUser.isAnonymous;
  const isLoginMode = mode === "login";
  // In loginOnly mode, an upgrade-mode response means the email wasn't found.
  const noAccountFound = loginOnly && step === "sent" && mode === "upgrade";

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to let the close animation finish
      const timer = setTimeout(reset, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, reset]);

  const handleSend = async () => {
    const success = await sendLink(email);
    if (success && onUpgradeComplete) {
      // The actual upgrade happens when user clicks the email link
      // onUpgradeComplete is called from CompleteUpgrade page
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {noAccountFound ? (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            ) : isLoginMode && step === "sent" ? (
              <LogIn className="w-5 h-5 text-[var(--accent)]" />
            ) : loginOnly ? (
              <LogIn className="w-5 h-5 text-[var(--accent)]" />
            ) : (
              <Shield className="w-5 h-5 text-[var(--accent)]" />
            )}
            <span>
              {alreadyUpgraded
                ? "Account Settings"
                : noAccountFound
                  ? "No account found"
                  : isLoginMode && step === "sent"
                    ? "Welcome back"
                    : loginOnly
                      ? "Sign in"
                      : "Secure Your Account"}
            </span>
          </DialogTitle>
          <DialogDescription>
            {alreadyUpgraded
              ? "Your account is already secured"
              : noAccountFound
                ? "We couldn't find an account for that email"
                : step === "sent"
                  ? isLoginMode
                    ? "We recognized your email — check your inbox to sign in"
                    : "Check your email for the magic link"
                  : loginOnly
                    ? "Enter the email linked to your existing account"
                    : "Enter your email to upgrade a new account or sign in to an existing one"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Already upgraded */}
          {alreadyUpgraded && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Account Secured</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Linked to <strong>{currentUser.email}</strong>
                </p>
              </div>
              <Button onClick={handleClose} variant="outline" className="w-full">
                Done
              </Button>
            </div>
          )}

          {/* Step 1: Benefits + email input */}
          {!alreadyUpgraded && (step === "idle" || step === "email" || step === "sending" || step === "error") && (
            <>
              {/* Benefits — only shown on the upgrade-from-settings entry,
                  not on the welcome-flow login-only entry */}
              {!loginOnly && (
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground text-sm">Why add an email?</h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-[var(--accent)] shrink-0" />
                      <span>Access from any device or browser</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-[var(--accent)] shrink-0" />
                      <span>Keep all your chat history forever</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-[var(--accent)] shrink-0" />
                      <span>No password needed — just a magic link</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-[var(--accent)] shrink-0" />
                      <span>Already registered? We'll sign you in instead</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Email input */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="upgrade-email">Email Address</Label>
                  <Input
                    id="upgrade-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !isPending && handleSend()}
                    disabled={isPending}
                    autoFocus
                    className="mt-1.5"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  onClick={handleSend}
                  disabled={!email.trim() || isPending}
                  className="w-full bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] text-white hover:opacity-90"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : loginOnly ? (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Send sign-in link
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Continue with email
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                {loginOnly
                  ? "We'll email you a secure sign-in link. No password needed."
                  : "We'll send a secure link to your email. New here? You'll be upgraded. Existing account? We'll sign you in. Either way — no password."}
              </p>
            </>
          )}

          {/* Step 2a: loginOnly + email not registered → "no account found" */}
          {!alreadyUpgraded && noAccountFound && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">No account found</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  We couldn't find an existing Vyzle account for <strong>{email}</strong>.
                  Go back and create a new account, or try a different email.
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => reset()} variant="outline" className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Try different email
                </Button>
                <Button onClick={handleClose} variant="outline" className="flex-1">
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* Step 2b: Email sent success (normal path) */}
          {!alreadyUpgraded && step === "sent" && !noAccountFound && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                {isLoginMode ? (
                  <LogIn className="w-8 h-8 text-[var(--accent)]" />
                ) : (
                  <Mail className="w-8 h-8 text-[var(--accent)]" />
                )}
              </div>

              <div>
                <h4 className="font-medium text-foreground">
                  {isLoginMode ? "Welcome back!" : "Check Your Email"}
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {isLoginMode ? (
                    <>
                      We recognized <strong>{email}</strong> as an existing Vyzle account.
                      We sent you a sign-in link — click it to access your chats on this device.
                    </>
                  ) : (
                    <>We sent a magic link to <strong>{email}</strong></>
                  )}
                </p>
              </div>

              <div className="text-xs text-muted-foreground space-y-1.5 text-left bg-muted/30 rounded-lg p-3">
                {isLoginMode ? (
                  <>
                    <p>1. Open the email and click the sign-in link</p>
                    <p>2. You'll be signed into your existing account</p>
                    <p>3. Your chats, rooms, and settings will load automatically</p>
                  </>
                ) : (
                  <>
                    <p>1. Open the email and click the link</p>
                    <p>2. Your account will be upgraded instantly</p>
                    <p>3. All your existing data stays linked</p>
                  </>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={() => reset()} variant="outline" className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Try different email
                </Button>
                <Button onClick={handleClose} variant="outline" className="flex-1">
                  Got it!
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
